import {
    composeWithDevTools as reduxComposeWithDevTools,
    EnhancerOptions as ReduxEnhancerOptions,
    devToolsEnhancer as ReduxDevToolsEnhancer,
} from 'redux-devtools-extension';
import { Action, applyMiddleware, compose, isAction, Middleware, StoreEnhancer, UnknownAction } from '@reduxjs/toolkit';

declare global {
    interface Window {
        /**
         * Часть тайпингов интерфейса Redux DevTools
         */
        __REDUX_DEVTOOLS_EXTENSION__?: {
            /**
             * Создает новый инстанс стора в Redux DevTools
             * Используется для сбора событий со всех модульных сторов
             *
             * @param options - Настройки созданного в Redux DevTools стора
             */
            connect(options: ReduxEnhancerOptions): {
                /**
                 * Отправляет новый экшен в Redux DevTools
                 *
                 * @param action - Экшен, который пришел в стор
                 * @param state - Новое состояние стора
                 */
                send(action: Action, state: unknown): void;
            };

            addDevToolsStore(storeName: string): void;
            removeDevToolsStore(storeName: string): void;
            clearDevToolsStores(): void;
        };
    }
}

if (process.env.BUILD_TARGET === 'client' && window.__REDUX_DEVTOOLS_EXTENSION__) {
    window.__REDUX_DEVTOOLS_EXTENSION__ = {
        ...window.__REDUX_DEVTOOLS_EXTENSION__,
        addDevToolsStore(storeName) {
            setDevToolsStores([...getDevToolsStores(), storeName]);
        },
        removeDevToolsStore(storeName) {
            setDevToolsStores(getDevToolsStores().filter(name => name !== storeName.toLowerCase()));
        },
        clearDevToolsStores() {
            localStorage.removeItem(devToolsLocalStorageKey);
        },
    };
}

export function isReduxDevToolsAvailable() {
    return Boolean(typeof window !== 'undefined' && window.__REDUX_DEVTOOLS_EXTENSION__);
}

type ModuleStore = {
    name: string;
    store: { getState(): unknown };
};

/**
 * Массив со всеми сторами, которые созданы в модулях. Используется для обхода и построения полного
 * состояния модульной части приложения
 */
let moduleStores: ModuleStore[] = [];

// Добавляет данные стора в общий стор (App Store)
export function registerDevToolsStore(moduleStore: ModuleStore) {
    moduleStores.push(moduleStore);
}

// Сбрасывает все зарегистрированные в DevTools сторы. Используется для очистки при переходах между story
export function cleanupRegisteredDevToolsStores() {
    moduleStores.length = 0;
}

const options = {
    name: 'App Store',
    // Для сериализации Map (используется в DNA)
    serialize: true,
    // Логировать стектрейсы, чтобы находить место вызова экшена (используется в DNA)
    trace: true,
    // @see: https://github.com/reduxjs/redux-devtools/blob/main/packages/redux-devtools-extension/src/index.ts#L13-L20
    latency: 500,
    // @see: https://github.com/reduxjs/redux-devtools/blob/main/packages/redux-devtools-extension/src/index.ts#L21-L26
    maxAge: 1000,
};

const composedStore = isReduxDevToolsAvailable()
    ? window.__REDUX_DEVTOOLS_EXTENSION__?.connect({
          ...options,
          name: `${window.origin} – ${options.name}`,
      })
    : null;

// Отфильтровывает нежелательные для общего стора экшены.
function filterAction(action: UnknownAction) {
    // Отфильтровываем внутренние экшены RTK Query (focused, unfocused, online, offline).
    if (action.type.startsWith('__rtkq/')) {
        return false;
    }

    return true;
}

/**
 * Redux-мидлвара, которая собирает экшены со всех модульных сторов и сливает их в общий стор (App Store).
 * Используется для повышения DX, т.к. в модульной архитектуре вместо одного общего стора используется
 * много маленьких модульных сторов
 *
 * @internal
 */
export const composedStoreMiddleware: Middleware = () => next => action => {
    const returnedValue = next(action);

    if (isAction(action) && filterAction(action)) {
        const debugState = moduleStores.reduce<Record<string, unknown>>((acc, { name, store }) => {
            acc[name] = store.getState();

            return acc;
        }, {});

        // Отправляем экшены в DevTools только после тогда, когда main thread будет свободен,
        // так как отправка стейта генерирует лонгтаск размеро в 150-300ms.
        // Это позволяет убрать тормоза интерфейса в режиме разработки.
        requestIdleCallback(() => {
            composedStore?.send(action, debugState);
        });
    }

    return returnedValue;
};

type EnhancerOptions = ReduxEnhancerOptions & {
    /**
     * Уникальное имя стора
     */
    name: string;
    /**
     * Название стора, отображаемое в DevTools.
     * @default значение поля "name"
     */
    title?: string;
};

/**
 * Devtools Enhancer для модульного стора
 *
 * @see https://github.com/reduxjs/redux-devtools/blob/main/extension/docs/API/Arguments.md
 */
export function getDevtoolsEnhancer(options: EnhancerOptions) {
    if (isReduxDevToolsAvailable() && isStoreDevToolsEnabled(options.name.toLowerCase())) {
        return ReduxDevToolsEnhancer({ ...options, name: options.title ?? options.name });
    }

    return null;
}

/**
 * Подключение мидлвар для redux-devtools и мидлвару для общего стора.
 *
 * @see https://github.com/reduxjs/redux-devtools/blob/main/extension/docs/API/Arguments.md
 */
export function composeWithDevTools(options: EnhancerOptions) {
    if (!isReduxDevToolsAvailable()) {
        return compose;
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    return ((...enhancers: StoreEnhancer[]) => {
        const composeFn = isStoreDevToolsEnabled(options.name.toLowerCase())
            ? reduxComposeWithDevTools({
                  ...options,
                  name: options.title ?? options.name,
              })
            : compose;

        return composeFn(...enhancers, applyMiddleware(composedStoreMiddleware));
    }) as typeof compose;
}

const devToolsLocalStorageKey = '_direct_devtools_modules_stores';

function isStoreDevToolsEnabled(storeName: string) {
    return Boolean(getDevToolsStores().some(name => name === storeName.toLowerCase()));
}

function getDevToolsStores() {
    const value = localStorage.getItem(devToolsLocalStorageKey);
    return value ? value.toLowerCase().split(',') : [];
}

function setDevToolsStores(stores: string[]) {
    localStorage.setItem(devToolsLocalStorageKey, stores.join(','));
}

function requestIdleCallback(callback: () => unknown) {
    if (window && window.requestIdleCallback) {
        window.requestIdleCallback(callback);
    } else {
        setTimeout(callback, 0);
    }
}
